import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, FormField, TextInput } from "@a2z/ui";
import { apiFetchClient } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { getErrorMessage } from "@/lib/api/errors";
import { defaultLocale, isSupportedLocale, keys } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";
import { PageLayout } from "@/shared/layout/PageLayout";
import type { StoreInvitePublicPreview } from "@/lib/api/types";

type PreviewState =
  | { status: "loading" }
  | { status: "invalid"; message?: string }
  | { status: "ready"; data: StoreInvitePublicPreview };

function resolveLocale(value: string | undefined): string {
  return value && isSupportedLocale(value) ? value : defaultLocale;
}

/**
 * Public landing page for store member invitations.
 *
 * - Anonymous + email has no Seller account → render signup form (name,
 *   phone, password). On success the server issues a session cookie and
 *   the page redirects into the new store.
 * - Anonymous + email already belongs to a Seller → render "sign in to
 *   accept" CTA pointing at the login page with a return URL.
 * - Authenticated → render a single-click "Accept" button. Server verifies
 *   the session email matches the invite email.
 */
export function StoreInviteAcceptPage() {
  const navigate = useNavigate();
  const t = useTranslations();
  const { locale: routeLocale, token = "" } = useParams();
  const locale = resolveLocale(routeLocale);
  const [preview, setPreview] = useState<PreviewState>({ status: "loading" });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await apiFetchClient(
          endpoints.public.storeInvite(token),
          { cache: "no-store" },
        );
        if (!response.ok) {
          const message = await response
            .json()
            .then(
              (body: { message?: string }) =>
                body?.message ?? "This invite is no longer valid.",
            )
            .catch(() => "This invite is no longer valid.");
          if (active) {
            setPreview({ status: "invalid", message });
          }
          return;
        }
        const data = (await response.json()) as StoreInvitePublicPreview;
        if (active) {
          setPreview({ status: "ready", data });
        }
      } catch {
        if (active) {
          setPreview({ status: "invalid" });
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [token]);

  if (preview.status === "loading") {
    return (
      <PageLayout title="Loading invite" logoAlt="Trioty">
        <div className="text-sm text-[color:var(--text-muted)]">
          {t(keys.invite.loading)}
        </div>
      </PageLayout>
    );
  }

  if (preview.status === "invalid") {
    return (
      <PageLayout title="Invite unavailable" logoAlt="Trioty">
        <div className="text-sm text-[color:var(--text-muted)]">
          {preview.message ?? "This invite is no longer valid."}
        </div>
      </PageLayout>
    );
  }

  const { data } = preview;
  const onAccepted = (storeSlug: string) => {
    navigate(`/${locale}/${storeSlug}`, { replace: true });
  };

  return (
    <PageLayout
      title={`Join ${data.storeName}`}
      subtitle={
        data.invitedByName
          ? `${data.invitedByName} invited you as ${data.roleNames.join(", ") || "a member"}.`
          : `You've been invited as ${data.roleNames.join(", ") || "a member"}.`
      }
      logoAlt="Trioty"
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4 text-sm">
          <div className="text-[color:var(--text-muted)]">
            {t(keys.invite.invitedEmail)}
          </div>
          <div className="font-semibold text-[color:var(--text-primary)]">
            {data.email}
          </div>
        </div>

        {data.hasExistingAccount ? (
          <ExistingAccountFlow
            token={token}
            email={data.email}
            locale={locale}
            onAccepted={onAccepted}
          />
        ) : (
          <SignupFlow token={token} email={data.email} onAccepted={onAccepted} />
        )}
      </div>
    </PageLayout>
  );
}

type AcceptResponse = {
  storeId: string;
  storeSlug: string;
  storeName: string;
};

function ExistingAccountFlow({
  token,
  email,
  locale,
  onAccepted,
}: {
  token: string;
  email: string;
  locale: string;
  onAccepted: (storeSlug: string) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const autoAcceptedRef = useRef(false);

  const t = useTranslations();

  const accept = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    const response = await apiFetchClient(
      endpoints.public.acceptStoreInvite(token),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      },
    );
    setSubmitting(false);
    if (!response.ok) {
      if (response.status === 401) {
        // Not signed in — bounce to login and return here with `?accept=1`, so
        // the acceptance auto-completes once authenticated (no second click).
        // The login form now honors `next` (previously it dropped the param,
        // stranding the invitee in their own store with the invite left
        // pending). Same-origin path only.
        const returnTo = `/${locale}/store-invite/${encodeURIComponent(token)}?accept=1`;
        window.location.href = `/${locale}/login?next=${encodeURIComponent(returnTo)}`;
        return;
      }
      setError(await getErrorMessage(response));
      return;
    }
    const data = (await response.json()) as AcceptResponse;
    onAccepted(data.storeSlug);
  }, [token, locale, onAccepted]);

  // Returning from login (?accept=1) → finish the acceptance automatically once.
  useEffect(() => {
    if (autoAcceptedRef.current) return;
    if (searchParams.get("accept") === "1") {
      autoAcceptedRef.current = true;
      void accept();
    }
  }, [searchParams, accept]);

  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm text-[color:var(--text-muted)]">
        {t(keys.invite.signInPrefix)} <strong>{email}</strong>{" "}
        {t(keys.invite.signInSuffix)}
      </div>
      {error ? <Alert message={error} /> : null}
      <Button onClick={accept} disabled={submitting}>
        {submitting ? "Accepting…" : "Accept invitation"}
      </Button>
    </div>
  );
}

function SignupFlow({
  token,
  email,
  onAccepted,
}: {
  token: string;
  email: string;
  onAccepted: (storeSlug: string) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const t = useTranslations();

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const formData = new FormData(event.currentTarget);
    const name = String(formData.get("name") ?? "").trim();
    const phone = String(formData.get("phone") ?? "").trim();
    const password = String(formData.get("password") ?? "");

    if (name.length < 2 || phone.length < 5 || password.length < 8) {
      setError(
        "Name, phone, and a password of at least 8 characters are required.",
      );
      return;
    }

    setSubmitting(true);
    const response = await apiFetchClient(
      endpoints.public.acceptStoreInvite(token),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, phone, password }),
      },
    );
    setSubmitting(false);
    if (!response.ok) {
      setError(await getErrorMessage(response));
      return;
    }
    const data = (await response.json()) as AcceptResponse;
    onAccepted(data.storeSlug);
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className="text-sm text-[color:var(--text-muted)]">
        {t(keys.invite.signupPrompt)}{" "}
        <strong>{email}</strong>
        {"."}
      </div>
      <FormField label="Full name" name="name" required>
        <TextInput name="name" autoComplete="name" required />
      </FormField>
      <FormField label="Phone" name="phone" required>
        <TextInput
          name="phone"
          autoComplete="tel"
          inputMode="tel"
          required
        />
      </FormField>
      <FormField label="Password" name="password" required>
        <TextInput
          name="password"
          type="password"
          autoComplete="new-password"
          required
        />
      </FormField>
      {error ? <Alert message={error} /> : null}
      <Button type="submit" disabled={submitting}>
        {submitting ? "Creating account…" : "Create account & join store"}
      </Button>
    </form>
  );
}
